import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Check,
  X,
  MessageSquare,
  Users,
  Calendar,
  FileText,
  Briefcase,
  Mail,
  Settings,
  Zap,
  Shield,
  Link2,
} from 'lucide-react';
import Logo from '../components/Logo';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'communication' | 'hr' | 'project' | 'productivity' | 'analytics';
  icon: React.ReactNode;
  color: string;
  connected: boolean;
}

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'slack',
      name: 'Slack',
      description: 'Enable proactive check-ins and engagement surveys through Slack channels',
      category: 'communication',
      icon: <MessageSquare size={24} />,
      color: '#4A154B',
      connected: true,
    },
    {
      id: 'teams',
      name: 'Microsoft Teams',
      description: 'Integrate engagement insights directly into Teams conversations',
      category: 'communication',
      icon: <Users size={24} />,
      color: '#6264A7',
      connected: false,
    },
    {
      id: 'workday',
      name: 'Workday',
      description: 'Sync employee data, org structure, and performance metrics',
      category: 'hr',
      icon: <Briefcase size={24} />,
      color: '#FF6B35',
      connected: false,
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      description: 'Connect sales team engagement with performance and quota data',
      category: 'analytics',
      icon: <Zap size={24} />,
      color: '#00A1E0',
      connected: false,
    },
    {
      id: 'bamboohr',
      name: 'BambooHR',
      description: 'Import employee records, time-off data, and organizational charts',
      category: 'hr',
      icon: <Users size={24} />,
      color: '#00A1E0',
      connected: false,
    },
    {
      id: 'greenhouse',
      name: 'Greenhouse',
      description: 'Track engagement from candidate stage through onboarding',
      category: 'hr',
      icon: <Users size={24} />,
      color: '#00A1E0',
      connected: false,
    },
    {
      id: 'jira',
      name: 'Jira',
      description: 'Correlate project workload and sprint velocity with engagement levels',
      category: 'project',
      icon: <FileText size={24} />,
      color: '#0052CC',
      connected: false,
    },
    {
      id: 'asana',
      name: 'Asana',
      description: 'Link task completion rates and project stress with disengagement signals',
      category: 'project',
      icon: <FileText size={24} />,
      color: '#F06A6A',
      connected: false,
    },
    {
      id: 'google-workspace',
      name: 'Google Workspace',
      description: 'Analyze calendar patterns, email activity, and collaboration metrics',
      category: 'productivity',
      icon: <Mail size={24} />,
      color: '#4285F4',
      connected: false,
    },
    {
      id: 'outlook',
      name: 'Microsoft Outlook',
      description: 'Track meeting frequency, response times, and calendar density',
      category: 'productivity',
      icon: <Mail size={24} />,
      color: '#0078D4',
      connected: false,
    },
    {
      id: 'okta',
      name: 'Okta',
      description: 'Leverage SSO data for authentication and user activity insights',
      category: 'productivity',
      icon: <Shield size={24} />,
      color: '#007DC1',
      connected: false,
    },
    {
      id: 'tableau',
      name: 'Tableau',
      description: 'Export engagement data for advanced analytics and custom dashboards',
      category: 'analytics',
      icon: <FileText size={24} />,
      color: '#E97627',
      connected: false,
    },
  ]);

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: 'All Integrations', count: integrations.length },
    { id: 'communication', label: 'Communication', count: integrations.filter(i => i.category === 'communication').length },
    { id: 'hr', label: 'HR & People', count: integrations.filter(i => i.category === 'hr').length },
    { id: 'project', label: 'Project Management', count: integrations.filter(i => i.category === 'project').length },
    { id: 'productivity', label: 'Productivity', count: integrations.filter(i => i.category === 'productivity').length },
    { id: 'analytics', label: 'Analytics', count: integrations.filter(i => i.category === 'analytics').length },
  ];

  const filteredIntegrations = integrations.filter(integration => {
    const matchesCategory = selectedCategory === 'all' || integration.category === selectedCategory;
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleToggleConnection = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? { ...integration, connected: !integration.connected }
          : integration
      )
    );
  };

  const connectedCount = integrations.filter(i => i.connected).length;

  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.header
        style={styles.header}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
        <div>
          <h1 style={styles.pageTitle}>Integrations</h1>
        <p style={styles.pageSubtitle}>
            Connect your business tools to unlock deeper engagement insights
        </p>
        </div>
      </motion.header>

      <div style={styles.content}>
        {/* Stats Bar */}
        <motion.div
          style={styles.statsBar}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={styles.statCard}>
            <div style={styles.statValue}>{connectedCount}</div>
            <div style={styles.statLabel}>Connected</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{integrations.length - connectedCount}</div>
            <div style={styles.statLabel}>Available</div>
              </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{integrations.length}</div>
            <div style={styles.statLabel}>Total Integrations</div>
          </div>
        </motion.div>

        {/* Search and Filters */}
          <motion.div
          style={styles.filtersSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          >
          <div style={styles.searchWrapper}>
            <input
              type="text"
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
                </div>

          <div style={styles.categoryFilters}>
            {categories.map((category) => (
              <button
                key={category.id}
                style={{
                  ...styles.categoryButton,
                  ...(selectedCategory === category.id ? styles.categoryButtonActive : {}),
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.label}
                <span style={styles.categoryCount}>({category.count})</span>
              </button>
            ))}
                    </div>
                  </motion.div>

        {/* Integrations Grid */}
                <motion.div
          style={styles.integrationsGrid}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {filteredIntegrations.map((integration, index) => (
            <motion.div
              key={integration.id}
              style={{
                ...styles.integrationCard,
                ...(integration.connected ? styles.integrationCardConnected : {}),
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4, boxShadow: '0 10px 40px rgba(105, 39, 127, 0.1)' }}
            >
              <div style={styles.integrationHeader}>
                <div
                  style={{
                    ...styles.integrationIcon,
                    background: `${integration.color}15`,
                    color: integration.color,
                  }}
                >
                  {integration.icon}
                  </div>
                <div style={styles.integrationInfo}>
                  <h3 style={styles.integrationName}>{integration.name}</h3>
                  <div style={styles.integrationStatus}>
                    {integration.connected ? (
                      <>
                        <Check size={14} color="#10B981" />
                        <span style={styles.statusTextConnected}>Connected</span>
                      </>
                    ) : (
                      <>
                        <Link2 size={14} color="#9CA3AF" />
                        <span style={styles.statusText}>Not Connected</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <p style={styles.integrationDescription}>{integration.description}</p>

              <div style={styles.integrationActions}>
                <button
                        style={{
                    ...styles.connectButton,
                    ...(integration.connected ? styles.disconnectButton : {}),
                        }}
                  onClick={() => handleToggleConnection(integration.id)}
                >
                  {integration.connected ? (
                    <>
                      <X size={16} />
                      Disconnect
                    </>
                  ) : (
                    <>
                      <Link2 size={16} />
                      Connect
                    </>
                          )}
                </button>
                {integration.connected && (
                  <button style={styles.settingsButton}>
                    <Settings size={16} />
                    Settings
                  </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
        </motion.div>

        {filteredIntegrations.length === 0 && (
          <motion.div
            style={styles.emptyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Link2 size={48} color="#D1D5DB" />
            <h3 style={styles.emptyTitle}>No integrations found</h3>
            <p style={styles.emptyText}>
              Try adjusting your search or filter criteria
            </p>
              </motion.div>
            )}

        {/* Info Section */}
        <motion.div
          style={styles.infoSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div style={styles.infoCard}>
            <Shield size={24} color="#69277F" />
            <div>
              <h4 style={styles.infoTitle}>Secure & Private</h4>
              <p style={styles.infoText}>
                All integrations use OAuth 2.0 and encrypted connections. Your data remains secure and private.
            </p>
            </div>
          </div>
          <div style={styles.infoCard}>
            <Zap size={24} color="#06BCCA" />
            <div>
              <h4 style={styles.infoTitle}>Real-time Sync</h4>
              <p style={styles.infoText}>
                Data syncs automatically in real-time, keeping your engagement insights up to date.
            </p>
            </div>
          </div>
          <div style={styles.infoCard}>
            <Settings size={24} color="#FF9C0D" />
            <div>
              <h4 style={styles.infoTitle}>Easy Configuration</h4>
              <p style={styles.infoText}>
                Set up integrations in minutes with our guided connection process.
            </p>
          </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#F8F9FC',
  },
  header: {
    padding: '24px 32px',
    background: 'white',
    borderBottom: '1px solid #E5E7EB',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'transparent',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  pageSubtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
  },
  statsBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '32px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    flex: 1,
    textAlign: 'center',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#6B7280',
  },
  filtersSection: {
    marginBottom: '32px',
  },
  searchWrapper: {
    marginBottom: '20px',
  },
  searchInput: {
    width: '100%',
    padding: '14px 20px',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    background: 'white',
    transition: 'all 0.2s ease',
  },
  categoryFilters: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  categoryButton: {
    padding: '10px 18px',
    background: 'white',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
  },
  categoryButtonActive: {
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderColor: '#69277F',
  },
  categoryCount: {
    fontSize: '12px',
    opacity: 0.7,
  },
  integrationsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  integrationCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    border: '2px solid transparent',
  },
  integrationCardConnected: {
    borderColor: '#10B981',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, rgba(16, 185, 129, 0.05) 100%)',
  },
  integrationHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '12px',
  },
  integrationIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  integrationInfo: {
    flex: 1,
  },
  integrationName: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 6px 0',
  },
  integrationStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
  },
  statusText: {
    color: '#9CA3AF',
  },
  statusTextConnected: {
    color: '#10B981',
    fontWeight: 500,
  },
  integrationDescription: {
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: 1.6,
    margin: '0 0 20px 0',
  },
  integrationActions: {
    display: 'flex',
    gap: '10px',
  },
  connectButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  disconnectButton: {
    background: '#FEE2E2',
    color: '#DC2626',
  },
  settingsButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px 16px',
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#4B5563',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '16px 0 8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  infoSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginTop: '32px',
  },
  infoCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  infoTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 8px 0',
  },
  infoText: {
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: 1.6,
    margin: 0,
  },
};

export default Integrations;
